import PropTypes from "prop-types"
import { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"

import debounce from "lodash.debounce"
import { useBrandedTranslation } from "../../../../../common/hooks/useBrandedTranslation"
import { useArticlesFilter } from "../../context/ArticlesFilterContext"
import HighlightedString from "../HighlightedString"

const REFRESH_DELAY = 400
const doubleRowReplacementInfoHeight = 54 // Use this value to determine when to show the button to expand
const maxRowReplacementInfoHeightCollapsed = 50
const maxRowReplacementInfoHeightExpanded = 400

const TrReplacementInfo = styled.div`
  flex-direction: column;
  position: relative;
  width: 100%;
  min-width: ${({ contentWidth }) => contentWidth}px !important;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[300]};
`

const ReplacementInfoText = styled.div`
  max-height: ${maxRowReplacementInfoHeightCollapsed}px;
  min-width: ${({ viewportWidth }) => viewportWidth}px;
  left: 0;
  right: 0;
  padding: ${({ theme }) =>
    `${2 * theme.sizes.base - 2}px ${4 * theme.sizes.base}px ${2 * theme.sizes.base - 2}px ${4 * theme.sizes.base}px`};
  border-right: 1px solid ${({ theme }) => theme.colors.gray[300]};
  z-index: 1;
  background: ${({ theme }) => theme.colors.replacementInfo};
  transition: max-height 0.3s ease-in;
  overflow: ${({ hasOverflowHidden }) => (hasOverflowHidden ? "hidden" : "unset")};
`

const Span = styled.span`
  position: ${({ isSticky }) => (isSticky ? "sticky" : "relative")};
  left: ${({ theme, isSticky }) => (isSticky ? theme.sizes.gapLarge : 0)}px;
`

const PseudoCell = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.gray[300]};
  height: 8px;
  background-color: ${({ theme }) => theme.colors.baseWhite};

  &.td--article-code {
    flex: none !important;
    position: sticky;
    left: 0;
  }
`

const FlexContainer = styled.div`
  display: flex;
`

const StyledLink = styled.span`
  color: ${({ theme }) => theme.colors.brand[500]};
  font-size: ${({ fontSize }) => fontSize}px;
  font-family: ${({ theme }) => theme.fontFamily.geberitBd};
  font-weight: 500;
  text-align: right; //this is only valid when the element is div
  cursor: pointer;
`

const ShowMore = styled.div`
  padding: 0 ${({ theme }) => 4 * theme.sizes.base - 2}px 0 0;
  position: absolute;
  z-index: ${({ theme }) => theme.zIndex.tooltipText};
  bottom: 10px;
  right: 0px;
  background: ${({ theme }) => theme.colors.replacementInfo};
`

const RowReplacementInfo = ({ isEven, contentWidth, viewportWidth, row }) => {
  const { t } = useBrandedTranslation()
  const articlesFilter = useArticlesFilter()
  const { state } = row.original
  const { replacedBy, replacementFor } = state
  const replacementInfoTextRef = useRef(null)
  const [showMoreReplacementInfo, setShowMoreReplacementInfo] = useState(false)
  const [isReplacementInfoCollapsed, setIsReplacementInfoCollapsed] = useState(true)

  let replacementInfoContent = replacedBy ? `${t("pdp-article-replacedBy")} ${replacedBy}` : ""
  replacementInfoContent += replacementFor && replacedBy ? ", " : ""
  replacementInfoContent += replacementFor ? `${t("pdp-article-replacementFor")} ${replacementFor}` : ""

  const getReplacementInfoHeight = () => {
    return replacementInfoTextRef?.current?.scrollHeight
  }

  const debouncedShowMoreReplacementInfoLines = useCallback(
    debounce(() => {
      setShowMoreReplacementInfo(getReplacementInfoHeight() > doubleRowReplacementInfoHeight)
    }, REFRESH_DELAY),
    []
  )

  useEffect(() => {
    debouncedShowMoreReplacementInfoLines()
    window.addEventListener("resize", debouncedShowMoreReplacementInfoLines)
    return () => {
      window.removeEventListener("resize", debouncedShowMoreReplacementInfoLines)
    }
  }, [])

  return (
    <TrReplacementInfo {...row.getRowProps()} isEven={isEven} contentWidth={contentWidth} viewportWidth={viewportWidth}>
      <ReplacementInfoText
        hasOverflowHidden={showMoreReplacementInfo}
        viewportWidth={viewportWidth}
        contentWidth={contentWidth}
        ref={replacementInfoTextRef}
        style={{
          maxHeight:
            !showMoreReplacementInfo || (showMoreReplacementInfo && !!isReplacementInfoCollapsed)
              ? `${maxRowReplacementInfoHeightCollapsed}px`
              : `${maxRowReplacementInfoHeightExpanded}px`,
        }}
      >
        <Span isSticky={!showMoreReplacementInfo}>
          <HighlightedString originalString={replacementInfoContent} highlightedString={articlesFilter} />
          {showMoreReplacementInfo && !isReplacementInfoCollapsed ? (
            <StyledLink as="div" fontSize={14} onClick={() => setIsReplacementInfoCollapsed(true)}>
              {t("show-less")}
            </StyledLink>
          ) : (
            ""
          )}
        </Span>
      </ReplacementInfoText>
      {showMoreReplacementInfo && isReplacementInfoCollapsed ? (
        <ShowMore>
          ...{" "}
          <StyledLink fontSize={14} onClick={() => setIsReplacementInfoCollapsed(false)}>
            {t("show-more")}
          </StyledLink>
        </ShowMore>
      ) : (
        ""
      )}
      <FlexContainer>
        {row.cells.map((cell, j) => {
          return (
            <PseudoCell
              key={j}
              {...cell.getCellProps()}
              className={`${j === 0 ? "td--article-code" : "td--article-attribute"}`}
            />
          )
        })}
      </FlexContainer>
    </TrReplacementInfo>
  )
}

export default RowReplacementInfo

RowReplacementInfo.propTypes = {
  isEven: PropTypes.bool,
  row: PropTypes.object,
  viewportWidth: PropTypes.number,
  contentWidth: PropTypes.number,
}
